<template>
    <div style="position: absolute; display: flex; flex-direction: row" :style="elementStyle">
        <div
            class="room-plan-table table"
            :style="rectangleStyle"
            @click="onElementClick"
            :class="[tableShapeClass, tableStatusClass, disabled ? 'disabled' : '']"
        >
            <div class="inner" :class="{ 'has-running-timer': runningTimerElapsed }">
                <div v-if="runningTimerElapsed" class="running-timer">
                    <i class="fa-light fa-timer mr-1"></i>{{ runningTimerElapsed }}
                </div>
                <div v-if="isRectangleTable" class="tablename text-truncate">{{ table.name }}</div>
                <div class="additionalinfo">
                    <div v-if="!isRectangleTable" class="tablename text-truncate">{{ table.name }}</div>
                    <template v-if="!hasMultipleBills">
                        <div class="usercount">
                            <i class="fal fa-user mr-1"></i>{{ table.manualEndUsersCount ?? table.allocations }}
                        </div>
                        <div v-if="table.timestamp" class="timestamp">
                            {{ DateTime.toTimeString(table.timestamp) }}
                        </div>
                        <div v-if="openBillAmount" class="amount">{{ toMoney(openBillAmount) }}</div>
                    </template>
                    <template v-else>
                        <div class="openbillscount">
                            <i class="fa-light fa-files mr-1"></i>{{ table.openBillsCount }}
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <div v-if="table.reservations.length > 0" style="display: flex; flex-direction: column; z-index: 1000">
            <div class="ml-1" v-for="reservation in table.reservations" v-bind:key="reservation.id">
                <div
                    @click="() => openOrderDetailsModal(reservation.orderId)"
                    class="reservation-period pb-1"
                    style="cursor: pointer"
                    :class="getReservationCssClass(reservation.status)"
                >
                    {{
                        DateTime.toTimeString(reservation.startDateTime) +
                        "-" +
                        DateTime.toTimeString(reservation.endDateTime)
                    }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { TableShape } from "@/data/model/Enums";
    import { type IRoomPlanTable, RoomPlanTableReservationStatus, RoomPlanTableStatus } from "@/data/model/Operator";
    import { DateTime } from "@/data/util/DateTime";
    import { Misc } from "@/data/util/Misc";
    import { applyTimerDiscounts, calculateTimerPrice, getTimerElapsedSeconds } from "@/data/util/TimerPrice";
    import { OrderDetailsModal } from "@/operator/modal/OrderDetailsModal";
    import { toMoney } from "@/shared/utils/format";
    import { useNow } from "@vueuse/core";
    import { computed, watch } from "vue";

    const props = defineProps<{
        table: IRoomPlanTable;
        disabled: boolean;
    }>();

    const tableShapeClass = computed(() => TableShape[props.table.shape].toLowerCase());
    const tableStatusClass = computed(() =>
        hasMultipleBills.value && props.table.reservations.length === 0
            ? "multiple-open-bills"
            : RoomPlanTableStatus[props.table.status].toLowerCase()
    );
    const isRectangleTable = computed(() => props.table.shape == TableShape.Rectangle);
    const elementStyle = computed(() => {
        if (props.table.reservations.length > 0) {
            return {
                width: "calc(" + props.table.dimension!.width + "%" + " + 60px)",
                height: props.table.dimension!.height + "%",
                top: props.table.dimension!.posY + "%",
                left: props.table.dimension!.posX + "%",
            };
        } else {
            return {
                width: props.table.dimension!.width + "%",
                height: props.table.dimension!.height + "%",
                top: props.table.dimension!.posY + "%",
                left: props.table.dimension!.posX + "%",
            };
        }
    });

    const rectangleStyle = computed(() => {
        if (props.table.reservations.length > 0) {
            return {
                width: "calc(100% - 60px)",
                height: "100%",
            };
        } else {
            return {
                width: "100%",
                height: "100%",
            };
        }
    });

    const hasMultipleBills = computed(() => props.table.openBillsCount > 1);

    // the longest running timer of the table as HH:MM, it shows no more than 99:00 however long it has been running
    const maxTimerMinutes = 99 * 60;
    // every second, like the bill view, so the amount of a running timer takes its CHF 0.50 steps at the same time there
    const { now, pause, resume } = useNow({ interval: 1000, controls: true });
    // the clock only runs while the table has a running timer
    watch(
        () => props.table.longestRunningTimerStartedDateTime !== undefined,
        (hasRunningTimer) => (hasRunningTimer ? resume() : pause()),
        { immediate: true }
    );
    const runningTimerElapsed = computed(() => {
        const startedDateTime = props.table.longestRunningTimerStartedDateTime;
        if (startedDateTime === undefined) return undefined;

        const elapsedMinutes = Math.floor(Math.max(0, now.value.getTime() - startedDateTime.getTime()) / 60000);
        const minutes = Math.min(elapsedMinutes, maxTimerMinutes);

        return `${Misc.padDigits(Math.floor(minutes / 60), 2)}:${Misc.padDigits(minutes % 60, 2)}`;
    });

    // the amount of the open bill as read, with the running timers priced on from there by the clock of the device, their
    // item discounts included, the same way the bill view prices them (getLiveUnitPriceAfterDiscount, data/stores/bill.ts)
    const openBillAmount = computed(() => {
        const amount = props.table.openBillAmount;
        if (amount === undefined) return undefined;

        let timersPriceGrowth = 0;
        for (const timer of props.table.openBillRunningTimers) {
            const elapsedSeconds = getTimerElapsedSeconds(timer.elapsedSeconds, timer.elapsedReadDateTime, true, now.value);
            const unitPriceAfterDiscount = applyTimerDiscounts(
                calculateTimerPrice(timer.minuteRate, elapsedSeconds),
                timer.discounts
            );
            timersPriceGrowth += timer.unpaidQuantity * (unitPriceAfterDiscount - timer.unitPriceAfterDiscount);
        }

        return amount + timersPriceGrowth;
    });

    const emit = defineEmits(["tableSelected"]);

    function onElementClick() {
        if (!props.disabled) {
            emit("tableSelected", props.table);
        }
    }

    function openOrderDetailsModal(orderId: string) {
        new OrderDetailsModal(orderId, () => {}).show();
    }

    function getReservationCssClass(status: RoomPlanTableReservationStatus | undefined): string {
        if (status != null) return RoomPlanTableReservationStatus[status!]?.toLowerCase() ?? "";
        else return "";
    }
</script>

<style lang="scss" scoped>
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .reservation-period {
        font-size: 10px;
        font-weight: bold;

        &.tentative,
        &.beforeproductionpartiallyarrived,
        &.beforeproductionarrived,
        &.afterpduntil15minafterftnotarrived,
        &.partiallyseated,
        &.seatedafternextpdstart {
            color: colors.$orange;
        }

        &.afterpduntil15minafterftpartiallyarrived,
        &.afterpdarrived,
        &.after15minafterftpartiallyarrived,
        &.seatedafternextftstart {
            color: colors.$red;
        }

        &.after15minafterftnotarrived {
            color: colors.$pink;
        }

        &.assignedbeforeoccupancy,
        &.seated {
            color: colors.$green;
        }
    }

    .room-plan-table {
        // the app bundle has no global border-box reset (portal/operator get one from metronic),
        // so without this the 6px padding is added to the 100% size and the table overflows its slot
        box-sizing: border-box;
        position: unset !important;
        background-color: #c9c9c9;
        border-radius: 5px;
        overflow: hidden;
        -webkit-box-shadow: 1px 1px 1px 0px rgba(87, 87, 87, 0.8);
        -moz-box-shadow: 1px 1px 1px 0px rgba(87, 87, 87, 0.8);
        box-shadow: 1px 1px 1px 0px rgba(87, 87, 87, 0.8);
        transition: background-color 0.3s variables.$easeOutExpo;
        // the font size is the single anchor for the whole tile: the views override it
        // on .room-plan-table so that the padding scales down with the text on small screens
        font-size: 12px;
        padding: 0.5em;
        font-family: Poppins, Helvetica, "Open Sans", sans-serif;
        cursor: pointer;

        > .inner {
            height: 100%;
            width: 100%;
            color: colors.$black;

            flex-direction: column;
            display: flex;

            position: relative;

            > .tablename {
                min-height: 1.25em;
            }

            // keeps the name of a rectangle table clear of the timer
            &.has-running-timer > .tablename {
                padding-right: 5.5em;
            }

            // the table has a running timer, in the top right corner of the tile
            > .running-timer {
                position: absolute;
                top: 0;
                right: 0;
                white-space: nowrap;
                line-height: 1.5em;
            }

            > .additionalinfo {
                margin: auto;
                text-align: center;

                > .fa-user {
                    margin-top: 2px;
                    margin-right: 2px;
                }

                .usercount {
                    margin-top: 0.25em;
                }

                .timestamp {
                    margin-top: 0.25em;
                }

                .timestamp,
                .amount {
                    line-height: 1.1em;
                }

                .openbillscount {
                    margin-top: 0.25em;
                    font-size: 1.25em;
                }
            }
        }

        &.round {
            border-radius: 50%;
            justify-content: center;
        }

        &.tentativereservation,
        &.assignedbeforestartofoccupancyduration {
            background-color: colors.$tastierLightGrey;
        }

        &.openbill,
        &.multiple-open-bills,
        &.guestsafterpduntil15minafterftpartiallyarrived,
        &.guestsafterpdarrived,
        &.guestsafter15minafterftpartiallyarrived,
        &.guestsseatedafternextftstart {
            background-color: colors.$red;
        }

        &.unfulfilledorder {
            background-color: colors.$fontColor;

            .inner {
                color: colors.$white;
            }
        }

        &.guestsafter15minafterftnotarrived {
            background-color: colors.$pink;
        }

        &.noordermade,
        &.guestsbeforeproductiondurationpartiallyarrived,
        &.guestsbeforeproductiondurationarrived,
        &.guestsafterpduntil15minafterftnotyarrived,
        &.guestspartiallyseated,
        &.guestsseatedafternextpdstart {
            background-color: colors.$orange;
        }

        &.guestsseated {
            background-color: colors.$green;
        }

        &.disabled {
            opacity: 0.2;
            cursor: default;
        }
    }
</style>
